import classOperation from '@/api/class-operation'

import { baseState, baseMutations } from '@/store/mixins'

export const state = {
  ...baseState,
  ac_status_id: [1, 3],
  editData: [],
  yearList: [],
  termList: [],
  teacherList: [],
  building: [],
  studyTimes: [],
  categories: [],
  classLists: [],
  ac_st_id: [],
  all_year: false,
  history: false,
  branch_id: null,

  // cascade filter selections
  acYearFilter: [],
  subProgramFilter: [],
  acTermFilter: [],
  isHasEnrollment: undefined,

  // cascade filter dropdown data
  yearListFilter: [], // populated when branch changes
  subProgramListFilter: [], // populated when academic year changes
  termListFilter: [] // populated when academic year changes (no longer depends on sub program)
}

export const getters = {}

export const mutations = {
  ...baseMutations,
  SET_EDIT_DATA(state, data) {
    state.editData = data
  },
  SET_YEAR_LIST(state, data) {
    state.yearList = data
  },
  SET_TERM_LIST(state, data) {
    state.termList = data
  },
  SET_TEACH_LIST(state, data) {
    state.teacherList = data
  },
  SET_BUILDING_LIST(state, data) {
    state.building = data
  },
  SET_STUDY_TIME_LIST(state, data) {
    state.studyTimes = data
  },
  SET_CATEGORY_LIST(state, data) {
    state.categories = data
  },
  SET_CLASS_LIST(state, data) {
    state.classLists = data ? data : []
  },
  SET_ALL_YEAR_FETCH(state, value) {
    state.all_year = value
  },
  SET_STATUS_ID(state, value) {
    state.ac_status_id = value
  },
  SET_AC_ST_ID(state, value) {
    state.ac_st_id = value
  },
  SET_BRANCH_ID(state, data) {
    state.branch_id = data
  },
  SET_HAS_ENROLMENT_FILTER(state, data) {
    state.isHasEnrollment = data
  },

  // cascade selections
  SET_ACYEAR_ID_FILTER(state, data) {
    state.acYearFilter = data
  },
  SET_SUBPROGRAM_ID_FILTER(state, data) {
    state.subProgramFilter = data
  },
  SET_ACTERM_ID_FILTER(state, data) {
    state.acTermFilter = data
  },

  SET_YEAR_LIST_FILTER(state, data) {
    state.yearListFilter = data || []
  },
  SET_SUB_PROGRAM_LIST_FILTER(state, data) {
    state.subProgramListFilter = data || []
  },
  SET_TERM_LIST_FILTER(state, data) {
    state.termListFilter = data || []
  },

  RESET_CASCADE_FROM_BRANCH(state) {
    state.acYearFilter = []
    state.subProgramFilter = []
    state.acTermFilter = []
    state.yearListFilter = []
    state.subProgramListFilter = []
    state.termListFilter = []
  },

  RESET_SELECTIONS_FROM_YEAR(state) {
    state.subProgramFilter = []
    state.acTermFilter = []
  },

  RESET_TERM_SELECTION(state) {
    state.acTermFilter = []
  }
}

export const actions = {
  async fetch(context, attrubutes = {}) {
    let params = Object.assign(
      {
        page: context.state.pagination.page,
        perPage: context.state.pagination.perPage,
        search: context.state.search,
        all_branch: Array.isArray(context.state.branch_id)
          ? context.state.branch_id.length
            ? undefined
            : true
          : true,
        filter: {
          ac_status_id: _.join(context.state.ac_status_id, ','),
          ac_st_id: _.join(context.state.ac_st_id, ','),
          ac_year_id: _.join(context.state.acYearFilter, ','),
          acps_id: _.join(context.state.subProgramFilter, ','),
          ac_term_id: _.join(context.state.acTermFilter, ','),
          has_enrollment: context.state.isHasEnrollment
        }
      },
      attrubutes
    )
    return await classOperation.get({ params }).then(response => {
      context.commit('RECEIVE_RESOURCES', response)
      return Promise.resolve(response)
    })
  },

  store(context, data) {
    return classOperation.store(data)
  },

  copyClass(context, data) {
    return classOperation.copyClass(data)
  },

  update(context, { id, data }) {
    return classOperation.update(id, data)
  },

  destroy(context, id) {
    return classOperation.destroy(id)
  },

  async find(context, id) {
    if (_.isEmpty(context.state.editData)) {
      let response = await classOperation.find(id)
      context.commit('SET_EDIT_DATA', response.data)
    }
  },

  async getListClass(context) {
    let response = await classOperation.formViewModel({
      params: { fnName: 'class_list' }
    })
    context.commit('SET_CLASS_LIST', response.data['class_list'])
  },

  async getAcademicYear(context) {
    let response = await classOperation.formViewModel({
      params: { fnName: 'academic_year' }
    })
    context.commit('SET_YEAR_LIST', response.data['academic_year'])
    return { data: response.data['academic_year'] }
  },

  /**
   * CASCADE: branch changed → fetch academic years for those branches
   * Also reload sub program and term with no filters (show all)
   */
  async getYearByBranch(context, branchIds = []) {
    context.commit('RESET_CASCADE_FROM_BRANCH')
    if (!branchIds.length) {
      return
    }
    const [yearRes, subRes, termRes] = await Promise.all([
      classOperation.formViewModel({
        params: { fnName: 'year_by_branch', branch_id: branchIds.join(',') }
      }),
      classOperation.formViewModel({
        params: { fnName: 'sub_program_by_year' }
      }),
      classOperation.formViewModel({
        params: { fnName: 'term_by_year' }
      })
    ])
    context.commit('SET_YEAR_LIST_FILTER', yearRes.data['year_by_branch'] || [])
    context.commit(
      'SET_SUB_PROGRAM_LIST_FILTER',
      subRes.data['sub_program_by_year'] || []
    )
    context.commit('SET_TERM_LIST_FILTER', termRes.data['term_by_year'] || [])
  },

  /**
   * AC Year changed → reload sub program and term filtered by year
   * Resets selections but keeps dropdowns populated
   */
  async getSubProgramByYear(context, yearIds = []) {
    context.commit('RESET_SELECTIONS_FROM_YEAR')
    const params = yearIds.length ? { ac_year_id: yearIds.join(',') } : {}
    const [subRes, termRes] = await Promise.all([
      classOperation.formViewModel({
        params: { fnName: 'sub_program_by_year', ...params }
      }),
      classOperation.formViewModel({
        params: { fnName: 'term_by_year', ...params }
      })
    ])
    context.commit(
      'SET_SUB_PROGRAM_LIST_FILTER',
      subRes.data['sub_program_by_year'] || []
    )
    context.commit('SET_TERM_LIST_FILTER', termRes.data['term_by_year'] || [])
  },

  /**
   * Sub Program changed → reload term filtered by sub program + ac year (if selected)
   * If neither is selected, shows all terms
   */
  async getTermByYear(context, { yearIds = [], acpsIds = [] } = {}) {
    context.commit('RESET_TERM_SELECTION')
    const params = {}
    if (yearIds.length) params.ac_year_id = yearIds.join(',')
    if (acpsIds.length) params.acps_id = acpsIds.join(',')
    const response = await classOperation.formViewModel({
      params: { fnName: 'term_by_year', ...params }
    })
    context.commit('SET_TERM_LIST_FILTER', response.data['term_by_year'] || [])
  },

  // kept for backwards compatibility
  async getTermBySubProgram(context, acpsIds = []) {
    return context.dispatch('getTermByYear', { acpsIds })
  },

  async dataBeforeCreate(context) {
    let response = await classOperation.formViewModel({
      params: {
        fnName:
          'study_time,academic_year,academic_term,teacher,building,academic_class_category'
      }
    })

    Object.keys(response.data).forEach(key => {
      if (key == 'academic_year') {
        context.commit('SET_YEAR_LIST', response.data[key])
      }
      if (key == 'academic_term') {
        context.commit('SET_TERM_LIST', response.data[key])
      }
      if (key == 'teacher') {
        context.commit('SET_TEACH_LIST', response.data[key])
      }
      if (key == 'building') {
        context.commit('SET_BUILDING_LIST', response.data[key])
      }
      if (key == 'study_time') {
        context.commit('SET_STUDY_TIME_LIST', response.data[key])
      }
      if (key == 'academic_class_category') {
        context.commit('SET_CATEGORY_LIST', response.data[key])
      }
    })
  },

  async getMainAndSub(context, acps_id) {
    let response = await classOperation.formViewModel({
      params: { fnName: 'sub_program_with_main', acps_id }
    })
    return { data: response.data.sub_program_with_main }
  },

  async getLevel(context, acps_id) {
    let response = await classOperation.formViewModel({
      params: { fnName: 'level_by_sub_program', acps_id }
    })
    return { data: response.data.level_by_sub_program }
  },

  async getClassAllowSwap(context, { acps_id, ac_year_id }) {
    let response = await classOperation.formViewModel({
      params: { fnName: 'class_allow_swap', acps_id, ac_year_id }
    })
    return { data: response.data.class_allow_swap }
  },

  async getStudyTime(context) {
    if (!context.state.studyTimes.length > 0) {
      let response = await classOperation.formViewModel({
        params: { fnName: 'study_time' }
      })
      context.commit('SET_STUDY_TIME_LIST', response.data.study_time)
    }
  },

  formViewModel(context, Options = {}) {
    return classOperation.formViewModel({ params: Options })
  }
}
